import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  ButtonSecondary,
  cssBorderRadius,
  cssColorValue,
  cssSpacing,
  screenSmMin,
  screenXsMax,
  semanticColors,
  spacer16,
  Text,
} from '@spotify-internal/encore-web';
import { useProperty } from '@spotify-internal/remote-config-integration-react';

import Metatags from 'components/Metatags/Metatags';
import PageContainer from 'components/PageContainer';
import Breadcrumbs from 'components/Breadcrumbs';
import BlockSerializer from 'lib/sanity/ui/BlockSerializer';
import WarningBanner from 'components/WarningBanner/WarningBanner';
import analytics from 'utils/client/analytics/analytics';
import { useUbiArticlePage } from 'utils/ubi/pages/useUbiArticle';
import { BlockSerializerUbiContext } from 'lib/sanity/ui/BlockSerializerUbiProvider';
import { TreatedArticleData, TreatmentState } from 'utils/remote-config/content/types';
import { AlternateLink } from 'data/market/types';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';
import Card from 'components/Card';
import { useUbiSearch } from 'utils/ubi/pages/useUbiSearch';
import Search from 'components/Search';
import IconChevron from 'components/IconChevron';
import { SharedPageData } from 'utils/shared-page-data';
import { enableCategoriesRedesign } from 'utils/remote-config/properties';
import { Vertical } from 'utils/vertical';

import FeedbackStickyWidget from './FeedbackStickyWidget';
import RelatedArticles from './RelatedArticles';
import CallToActionCard from './CallToActionCard';
import CommunityCallToAction from './CommunityCallToAction';
import { ArticleCategories } from './Categories';
import { ArticleCategoriesSmallView } from './Categories/SmallScreenView';

export interface ArticleProps {
  article: TreatedArticleData;
  articleSlug: string;
  alternateLinks: AlternateLink[];
  showCommunityLink: boolean;
  sharedPageData: SharedPageData;
}

const Title = styled(Text)`
  word-break: break-word;
  hyphens: auto;
`;

// CTAs might be on top/bottom of the article content
// Setting the outer container use `flex`
// so that child elements can set `order` value for placement
const OrderedSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: main;
`;

const CommunityCallToActionWithMargin = styled(CommunityCallToAction)`
  margin: ${cssSpacing('looser')} 0;
  @media (min-width: ${screenSmMin}) {
    grid-area: community-cta;
  }
`;

const CategoriesSmallViewTrigger = styled(ButtonSecondary)`
  background-color: ${cssColorValue(semanticColors.backgroundElevatedBase)};
  border: 0;
  border-radius: ${cssBorderRadius('larger-2')};
  justify-content: flex-start;
  margin-block-start: ${cssSpacing('looser-2')};
  padding-inline-start: 8px;

  @media (min-width: ${screenSmMin}) {
    display: none;
  }
`;

const CategoriesDesktopWrapper = styled.div`
  display: none;

  @media (min-width: ${screenSmMin}) {
    display: flex;
    flex-shrink: 0;
    grid-area: nav;
  }
`;

const PAGE_CONTAINER_MAX_WIDTH = 992;

const sidebarVariantLayout = css`
  max-width: ${PAGE_CONTAINER_MAX_WIDTH}px;
  @media (min-width: ${screenSmMin}) {
    display: grid;
    grid-template-areas:
      'search-and-breadcrumbs .'
      'main nav'
      'feedback-widget feedback-widget'
      'community-cta community-cta';
    grid-template-columns: 1fr 305px;
    grid-template-rows: 1fr;
    column-gap: 80px;
  }
`;

const StyledPageContainer = styled(PageContainer)<{ $sidebarVariant?: boolean }>`
  ${({ $sidebarVariant }) => $sidebarVariant && sidebarVariantLayout}
`;

const HeadContainer = styled.div`
  @media (min-width: ${screenSmMin}) {
    grid-area: search-and-breadcrumbs;
  }
`;

const StyledFeedbackStickyWidget = styled(FeedbackStickyWidget)`
  @media (min-width: ${screenSmMin}) {
    grid-area: feedback-widget;
  }
`;

function getTreatmentIdentifier(article: TreatedArticleData, articleSlug: string): string {
  switch (article.treatmentState) {
    case TreatmentState.Control:
      return `${articleSlug}_control`;
    case TreatmentState.Treatment:
      return `${articleSlug}_treatment_${article.treatmentKey}`;
    default:
      return articleSlug;
  }
}

const Article = ({
  article,
  articleSlug,
  showCommunityLink,
  alternateLinks = [],
  sharedPageData: { banner, categories },
}: ArticleProps) => {
  const articleIdentifier = getTreatmentIdentifier(article, articleSlug);
  const ubi = useUbiArticlePage(articleIdentifier);
  const ubiSearch = useUbiSearch();
  const {
    isPlain,
    verticalConfig: { verticalName },
  } = useServerSideConstants();
  const [displayCategories, setDisplayCategories] = useState(false);

  const isCategoriesNavigationEnabled = useProperty(enableCategoriesRedesign);

  const shouldRenderCategoryNavigation = isCategoriesNavigationEnabled && verticalName === Vertical.CONSUMER;

  const toggleCategories = () => {
    setDisplayCategories(!displayCategories);
  };

  useEffect(() => {
    const handleResize = () => {
      if (!shouldRenderCategoryNavigation) {
        return;
      }

      if (window.innerWidth > Number(screenXsMax.slice(0, 3)) && displayCategories) {
        setDisplayCategories(false);
      }
    };

    window.addEventListener('resize', handleResize, false);

    return () => {
      window.removeEventListener('resize', handleResize, false);
    };
  }, [displayCategories, shouldRenderCategoryNavigation]);

  useEffect(() => {
    if (articleSlug !== articleIdentifier) {
      analytics.trackExperiment(articleIdentifier);
    }
    ubi.logArticleImpression();
  }, [articleIdentifier, ubi, articleSlug]);

  const { content, title, relatedArticles, category, callToActionCard } = article;

  const parentCategory = category?.parentCategory;

  // Take the `callToActionCard` directly referenced by the article first
  // Then look up on its `parentCategory`
  const prioCallToActionCard = callToActionCard || category?.parentCategory?.callToActionCard;

  return (
    <>
      <Metatags title={title as string} urlPath={`article/${articleSlug}`} alternateLinks={alternateLinks} />

      {!isPlain && <WarningBanner banner={banner} />}

      {displayCategories ? (
        <ArticleCategoriesSmallView categories={categories} onBackToArticle={toggleCategories} />
      ) : (
        <StyledPageContainer $sidebarVariant={shouldRenderCategoryNavigation}>
          <HeadContainer>
            {/* Contact us article is under an experiment, can remove it once it's done */}
            {articleSlug.toLowerCase() !== 'contact-us' && <Search renderAsOverlay ubi={ubiSearch} />}

            {!isPlain && parentCategory && <Breadcrumbs parentCategory={parentCategory} article={article} ubi={ubi} />}
          </HeadContainer>

          <OrderedSectionContainer>
            {/* Do not show if `title` is not localized  */}
            {!isPlain && prioCallToActionCard?.title && <CallToActionCard {...prioCallToActionCard} ubi={ubi} />}
            {shouldRenderCategoryNavigation && (
              <CategoriesSmallViewTrigger
                onClick={toggleCategories}
                fullWidth
                size="medium"
                iconTrailing={() => <IconChevron size={24} />}
              >
                {parentCategory?.title}
              </CategoriesSmallViewTrigger>
            )}

            {content && (
              <Card>
                <Title forwardedAs="h1" variant="titleLarge" paddingBottom={spacer16}>
                  {title}
                </Title>
                <BlockSerializerUbiContext.Provider value={ubi}>
                  <BlockSerializer value={content} />
                </BlockSerializerUbiContext.Provider>
              </Card>
            )}

            {!isPlain && relatedArticles.length > 0 && (
              <Card>
                <RelatedArticles relatedArticles={relatedArticles} ubi={ubi} />
              </Card>
            )}
          </OrderedSectionContainer>

          {shouldRenderCategoryNavigation && (
            <CategoriesDesktopWrapper>
              <ArticleCategories categories={categories} />
            </CategoriesDesktopWrapper>
          )}
          <StyledFeedbackStickyWidget articleIdentifier={articleIdentifier} ubi={ubi} />
          {showCommunityLink && !isPlain && <CommunityCallToActionWithMargin ubi={ubi} />}
        </StyledPageContainer>
      )}
    </>
  );
};

export default Article;
